import React, {
	useRef, useState, useEffect, useCallback,
} from 'react';
import classNames from 'classnames';

interface LiveCounterProps {
	notLive?: boolean;
	isHost?: boolean;
	isHeader?: boolean;
	startTime?: any;
	secondCallback?: (time: number) => void;
}

export default function LiveCounter({
	notLive, isHost, isHeader, startTime, secondCallback,
}: LiveCounterProps) {
	const startTimer = useRef(Date.now());
	const [elapsed, setElapsed] = useState(0);
	const timeout = useRef<any>(null);
	const mounted = useRef(false);
	const time = Math.abs(startTimer.current - Date.now());
	const seconds = useRef<number>(0);

	if (notLive) {
		startTimer.current = Date.now();
	} else if (startTime && startTimer.current) {
		// If in the middle of a live broadcast or a scheduled rehearsal and the page is
		// loaded/refreshed, we want the time elapsed on the counter to start from the beginning
		// of the broadcast/rehearsal.
		startTimer.current = startTime;
	}

	useEffect(() => {
		if (timeout.current) {
			clearTimeout(timeout.current);
		}

		timeout.current = setTimeout(() => {
			if (mounted.current) {
				setElapsed(elapsed + 200);

				const roundedTime = Math.floor(time / 1000);

				if (roundedTime % 1 === 0) {
					seconds.current = roundedTime * 1000;
				}
			}
		}, 200);
	});

	useEffect(() => {
		secondCallback?.(seconds.current);
	}, [seconds.current]);

	useEffect(() => {
		// we want to prevent the elapsed counter from updating if this component is not mounted
		mounted.current = true;

		return () => {
			mounted.current = false;

			if (timeout.current) {
				clearTimeout(timeout.current);
			}
		};
	}, []);

	function minutes() {
		if (notLive) {
			if (isHost) {
				return '00';
			}
			if (isHeader) {
				return '00m';
			}
			return '00';
		}

		// Total mins elapsed and then % 60 because any mins > 60 = hours
		const mins = Math.floor((time / 1000 / 60)) % 60;
		if (mins < 10) return `0${mins}`;
		return mins;
	}

	function getSeconds() {
		if (notLive) return '00';

		const seconds = Math.floor((time / 1000) % 60);
		if (seconds < 10) {
			return `0${seconds}`;
		}
		return seconds;
	}

	const getHours = useCallback((headerSpacing: boolean = false): string => {
		if (notLive) {
			return '';
		}

		const hours = Math.floor(time / 1000 / 60 / 60);

		if (hours > 0) {
			return `${hours}${headerSpacing ? ' : ' : ':'}`;
		}

		return '';
	}, [notLive, time]);

	return (
		// This timer is used in the header (different for host v. presenter)
		// and above main video for live presenter
		<div className={classNames('live-indicator', !isHeader && 'presenter-main-video', { 'not-live': notLive || isHeader })}>
			<span className={!isHeader && !isHost && !notLive ? 'timer-text-presenter-live' : 'timer-text'}>
				{isHeader
					? <span>{getHours()}{minutes()}:{getSeconds()}</span>
					: <span>{getHours(true)}{minutes()} : {getSeconds()}</span>
				}
			</span>
		</div>
	);
}
